import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
    ModalOverlay, Select, Stack, Textarea,
} from "@chakra-ui/react";
import PaymentLinkSuccessPanel from "./PaymentLinkSuccessPanel";
import { sendPaymentLink } from "./paymentsApi";
import { PAYMENT_CURRENCY_OPTIONS } from "./paymentsConstants";
import PaymentField from "./PaymentField";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns the empty initial form state.
 * @returns {Object}
 */
function buildInitialForm() {
    return { client_email: "", client_name: "", description: "", amount: "", currency: "eur", reference: "" };
}

/**
 * Validates form inputs before submission.
 * @param {Object} form - Form state
 * @returns {string|null} Error or null
 */
function validateForm(form) {
    if (!form.client_email || !EMAIL_REGEX.test(form.client_email)) return "Email client invalide";
    const amount = Number(form.amount);
    if (!form.amount || Number.isNaN(amount) || amount < 1) return "Montant minimum : 1";
    return null;
}

/**
 * Builds the API payload (omits empty optional fields).
 * @param {Object} form - Form state
 * @returns {Object}
 */
function buildPayload(form) {
    const payload = { client_email: form.client_email.trim(), amount: Number(form.amount), currency: form.currency || "eur" };
    if (form.client_name?.trim()) payload.client_name = form.client_name.trim();
    if (form.description?.trim()) payload.description = form.description.trim();
    if (form.reference?.trim()) payload.reference = form.reference.trim();
    return payload;
}

/**
 * Submits the form and stores the resulting transaction so the success
 * panel can display the Stripe Checkout URL.
 * @param {Object} args - { form, setLoading, setResult, onSent }
 */
async function submit({ form, setLoading, setResult, onSent }) {
    const err = validateForm(form);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        const data = await sendPaymentLink(buildPayload(form));
        toast.success("Lien de paiement généré");
        setResult(data);
        onSent?.(data);
    } catch (error) {
        const msg = error?.response?.data || "Échec de la génération";
        toast.error(typeof msg === "string" ? msg : "Échec de la génération");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the amount + currency row used inside the form.
 * @param {Object} props - { form, onChange }
 * @returns {React.ReactElement}
 */
function AmountRow({ form, onChange }) {
    return (
        <Stack direction={{ base: "column", sm: "row" }} spacing="16px">
            <PaymentField label="Montant" type="number" value={form.amount} isRequired
                onChange={(v) => onChange("amount", v)} />
            <PaymentField label="Devise">
                <Select value={form.currency} borderRadius="12px" onChange={(e) => onChange("currency", e.target.value)}>
                    {PAYMENT_CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                </Select>
            </PaymentField>
        </Stack>
    );
}

/**
 * Renders the form fields used to create a new payment link.
 * @param {Object} props - { form, onChange }
 * @returns {React.ReactElement}
 */
function LinkFormFields({ form, onChange }) {
    return (
        <Stack spacing="16px">
            <PaymentField label="Email client" type="email" value={form.client_email} isRequired
                onChange={(v) => onChange("client_email", v)} />
            <PaymentField label="Nom client (optionnel)" value={form.client_name}
                onChange={(v) => onChange("client_name", v)} />
            <PaymentField label="Description" helper="Visible par le client dans Stripe Checkout">
                <Textarea value={form.description} onChange={(e) => onChange("description", e.target.value)} rows={2} borderRadius="12px" />
            </PaymentField>
            <AmountRow form={form} onChange={onChange} />
            <PaymentField label="Référence interne (optionnel)" value={form.reference}
                onChange={(v) => onChange("reference", v)} helper="Auto-générée si vide" />
        </Stack>
    );
}

/**
 * Builds the dialog footer buttons depending on success state.
 * @param {Object} props - { result, loading, onClose, onSubmit }
 * @returns {React.ReactElement}
 */
function DialogActionsRow({ result, loading, onClose, onSubmit }) {
    if (result) return <Button colorScheme="brand" onClick={onClose}>Fermer</Button>;
    return (
        <>
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Génération...">Générer le lien</Button>
        </>
    );
}

/**
 * Dialog generating a Stripe Checkout payment link for a B2B sale.
 * @param {Object} props - { open, onClose, onSent }
 * @returns {React.ReactElement}
 */
function SendPaymentLinkDialog({ open, onClose, onSent }) {
    const [form, setForm] = useState(buildInitialForm());
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => { if (open) { setForm(buildInitialForm()); setResult(null); } }, [open]);
    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submit({ form, setLoading, setResult, onSent });
    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Lien de paiement commercial</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {result ? <PaymentLinkSuccessPanel result={result} /> : <LinkFormFields form={form} onChange={onChange} />}
                </ModalBody>
                <ModalFooter gap="8px">
                    <DialogActionsRow result={result} loading={loading} onClose={onClose} onSubmit={onSubmit} />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default SendPaymentLinkDialog;
